import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  Plus, Pencil, Trash2, X,
} from 'lucide-react';
import { Action, ErrorNote, Loading, Panel, PanelHead, SearchField } from '../../components/aa';
import { hasScreens } from '../../lib/rolesWithoutScreens';
import { getRoles, getPermissions, createRole, updateRole, deleteRole } from '../../services/api';
import RoleRegistryPanel from './RoleRegistryPanel';
import { ClientGrantOverrides } from '../orbit-room/ClientGrantOverrides';

interface Role {
    id: string;
    name: string;
    description: string;
    permissions: string[];
}

interface Permission {
    id: string;
    name: string;
    description: string;
}

const CHIP_LIMIT = 6;

const RoleManagement: React.FC = () => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Add role modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [newRole, setNewRole] = useState<{ name: string; description: string; permissions: string[] }>({
        name: '',
        description: '',
        permissions: []
    });

    // Edit role modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editRole, setEditRole] = useState<Role | null>(null);

    useEffect(() => {
        fetchRoles();
        fetchPermissions();
    }, []);

    const fetchRoles = async () => {
        try {
            const data: Role[] = await getRoles();
            setRoles(data.filter(r => hasScreens(r.name)));
            setLoading(false);
        } catch (err) {
            console.error('Error fetching roles:', err);
            toast.error('Failed to fetch roles');
            setError('Failed to fetch roles');
            setLoading(false);
        }
    };

    const fetchPermissions = async () => {
        try {
            const data = await getPermissions();
            setPermissions(data);
        } catch (err) {
            console.error('Error fetching permissions:', err);
            toast.error('Failed to fetch permissions');
        }
    };

    // --- Searched ---
    const searched = useMemo(() => {
        if (!searchQuery.trim()) return roles;
        const q = searchQuery.toLowerCase();
        return roles.filter(r =>
            r.name.toLowerCase().includes(q) ||
            r.description.toLowerCase().includes(q)
        );
    }, [roles, searchQuery]);

    const handleDelete = async (roleId: string) => {
        if (window.confirm('Are you sure you want to delete this role?')) {
            try {
                await deleteRole(roleId);
                setRoles(roles.filter(role => role.id !== roleId));
                toast.success('Role deleted successfully');
            } catch (err) {
                console.error('Error deleting role:', err);
                toast.error('Failed to delete role');
            }
        }
    };

    const handleAddRole = async () => {
        try {
            if (!newRole.name.trim()) {
                toast.error('Role name is required');
                return;
            }
            if (newRole.permissions.length === 0) {
                toast.error('At least one permission must be selected');
                return;
            }
            const data = await createRole(newRole);
            setRoles([...roles, data]);
            setShowAddModal(false);
            resetNewRole();
            toast.success('Role created successfully');
        } catch (err) {
            console.error('Error creating role:', err);
            toast.error('Failed to create role');
        }
    };

    const handleOpenEditModal = (role: Role) => {
        setEditRole({ ...role });
        setShowEditModal(true);
    };

    const handleEditRole = async () => {
        if (!editRole) return;
        try {
            if (!editRole.name.trim()) {
                toast.error('Role name is required');
                return;
            }
            if (editRole.permissions.length === 0) {
                toast.error('At least one permission must be selected');
                return;
            }
            const data = await updateRole(editRole.id, {
                name: editRole.name,
                description: editRole.description,
                permissions: editRole.permissions
            });
            setRoles(roles.map(role => role.id === editRole.id ? data : role));
            setShowEditModal(false);
            setEditRole(null);
            toast.success('Role updated successfully');
        } catch (err) {
            console.error('Error updating role:', err);
            toast.error('Failed to update role');
        }
    };

    const resetNewRole = () => {
        setNewRole({ name: '', description: '', permissions: [] });
    };

    const togglePermission = (permissionId: string, isEdit = false) => {
        if (isEdit && editRole) {
            if (editRole.permissions.includes(permissionId)) {
                setEditRole({ ...editRole, permissions: editRole.permissions.filter(id => id !== permissionId) });
            } else {
                setEditRole({ ...editRole, permissions: [...editRole.permissions, permissionId] });
            }
        } else {
            if (newRole.permissions.includes(permissionId)) {
                setNewRole({ ...newRole, permissions: newRole.permissions.filter(id => id !== permissionId) });
            } else {
                setNewRole({ ...newRole, permissions: [...newRole.permissions, permissionId] });
            }
        }
    };

    // ─── Render ───
    let tableBody: React.ReactNode = searched.map(role => {
        const shown = role.permissions.slice(0, CHIP_LIMIT);
        const extra = role.permissions.length - shown.length;
        return (
            <div
                key={role.id}
                className="grid grid-cols-[minmax(140px,1fr)_minmax(0,3fr)_auto] items-center gap-4 border-t border-line-soft py-3"
            >
                <div className="text-[13px] font-semibold text-ink">{role.name}</div>
                <div className="flex flex-wrap gap-1.5">
                    {shown.map((permission) => {
                        const permObject = permissions.find(p => p.id === permission);
                        let displayName = permission;
                        if (permObject) displayName = permObject.name;
                        return (
                            <span
                                key={permission}
                                className="rounded-md bg-canvas-inset px-2 py-0.5 text-[11.5px] font-medium text-ink-muted"
                                title={permObject?.description}
                            >
                                {displayName}
                            </span>
                        );
                    })}
                    {extra > 0 && (
                        <button
                            type="button"
                            onClick={() => handleOpenEditModal(role)}
                            className="rounded-md px-2 py-0.5 text-[11.5px] font-semibold text-ink hover:bg-canvas-inset"
                        >
                            +{extra} more
                        </button>
                    )}
                    {role.permissions.length === 0 && <span className="text-[12px] text-ink-soft">No permissions</span>}
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => handleOpenEditModal(role)}
                        className="grid h-9 w-9 place-items-center rounded-lg text-ink-soft transition-colors hover:bg-canvas-inset hover:text-ink"
                        title="Edit role"
                        aria-label={`Edit ${role.name}`}
                    >
                        <Pencil size={16} />
                    </button>
                    <button
                        type="button"
                        onClick={() => handleDelete(role.id)}
                        className="grid h-9 w-9 place-items-center rounded-lg text-ink-soft transition-colors hover:bg-danger-soft hover:text-danger"
                        title="Delete role"
                        aria-label={`Delete ${role.name}`}
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>
        );
    });
    if (searched.length === 0) {
        tableBody = <p className="border-t border-line-soft py-10 text-center text-[13px] text-ink-dim">No roles match.</p>;
    }
    if (error) {
        tableBody = <div className="py-4"><ErrorNote message={error} /></div>;
    }
    if (loading) {
        tableBody = <Loading label="Loading roles" />;
    }

    return (
        <div className="space-y-5">
            <RoleRegistryPanel />

            <Panel>
                <PanelHead
                    title="Permissions by role"
                    desc={`The platform default for what each role can do. ${permissions.length} permissions available.`}
                    action={(
                        <Action size="md" variant="quiet" icon={<Plus size={15} />} onClick={() => setShowAddModal(true)}>
                            Add role
                        </Action>
                    )}
                />
                <div className="px-5 pb-3 pt-4 md:px-6">
                    <SearchField value={searchQuery} onChange={setSearchQuery} placeholder="Search roles" />
                    <div className="mt-3 overflow-x-auto">
                        <div className="min-w-[560px]">{tableBody}</div>
                    </div>
                </div>
            </Panel>

            <ClientGrantOverrides />

            {/* Add Role Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between flex-wrap gap-3 px-6 py-4 border-b border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-900">Add New Role</h2>
                            <button
                                onClick={() => { setShowAddModal(false); resetNewRole(); }}
                                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-6 space-y-4 overflow-y-auto flex-1">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Role Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newRole.name}
                                    onChange={(e) => setNewRole({...newRole, name: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400"
                                    placeholder="Enter role name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    value={newRole.description}
                                    onChange={(e) => setNewRole({...newRole, description: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400"
                                    placeholder="Enter role description"
                                    rows={3}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Permissions <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto p-3 border border-gray-200 rounded-lg bg-gray-50/50">
                                    {permissions.map((permission) => (
                                        <label key={permission.id} className="flex items-start gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={newRole.permissions.includes(permission.id)}
                                                onChange={() => togglePermission(permission.id)}
                                                className="mt-0.5 h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                                            />
                                            <div>
                                                <span className="text-sm text-gray-700">{permission.name}</span>
                                                <span className="block text-xs text-gray-400">{permission.description}</span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl">
                            <button
                                onClick={() => { setShowAddModal(false); resetNewRole(); }}
                                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddRole}
                                className="px-4 py-2 text-sm text-white bg-black rounded-lg hover:bg-gray-800 transition-colors"
                            >
                                Create Role
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Role Modal */}
            {showEditModal && editRole && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between flex-wrap gap-3 px-6 py-4 border-b border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-900">Edit Role</h2>
                            <button
                                onClick={() => { setShowEditModal(false); setEditRole(null); }}
                                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-6 space-y-4 overflow-y-auto flex-1">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Role Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={editRole.name}
                                    onChange={(e) => setEditRole({...editRole, name: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400"
                                    placeholder="Enter role name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    value={editRole.description}
                                    onChange={(e) => setEditRole({...editRole, description: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400"
                                    placeholder="Enter role description"
                                    rows={3}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Permissions <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto p-3 border border-gray-200 rounded-lg bg-gray-50/50">
                                    {permissions.map((permission) => (
                                        <label key={permission.id} className="flex items-start gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={editRole.permissions.includes(permission.id)}
                                                onChange={() => togglePermission(permission.id, true)}
                                                className="mt-0.5 h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                                            />
                                            <div>
                                                <span className="text-sm text-gray-700">{permission.name}</span>
                                                <span className="block text-xs text-gray-400">{permission.description}</span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl">
                            <button
                                onClick={() => { setShowEditModal(false); setEditRole(null); }}
                                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleEditRole}
                                className="px-4 py-2 text-sm text-white bg-black rounded-lg hover:bg-gray-800 transition-colors"
                            >
                                Update Role
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoleManagement;
